import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtMoney, AUCTION_HOUSES, TITLE_STATUS, VEHICLE_STATUS } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Search, Pencil, Trash2, Wand2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  vin: "", year: new Date().getFullYear(), make: "", model: "", trim: "", mileage: 0,
  engine: "", transmission: "", fuel_type: "", color: "", title_status: "clean",
  purchase_date: new Date().toISOString().slice(0, 10), purchase_price: 0, purchase_location: "",
  auction_house: "", seller_info: "",
  auction_fees: 0, transport_fees: 0, gate_fees: 0, documentation_fees: 0, storage_fees: 0, buyer_fees: 0, misc_costs: 0,
  market_value: 0, status: "purchased", notes: "",
};

export default function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [decoding, setDecoding] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "employee";
  const canDelete = user?.role === "admin";

  const load = useCallback(async () => {
    const r = await api.get("/vehicles", { params: status !== "all" ? { status } : {} });
    setItems(r.data);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return ["vin", "make", "model", "trim", "stock_number", "color"].some((k) => (v[k] || "").toString().toLowerCase().includes(s));
  });

  const decodeVin = async () => {
    if (!form.vin) { toast.error("Enter a VIN first"); return; }
    setDecoding(true);
    try {
      const r = await api.get(`/vin/${form.vin}`);
      setForm({ ...form, ...r.data });
      toast.success("VIN decoded");
    } catch (e) { toast.error("VIN decode failed"); }
    finally { setDecoding(false); }
  };

  const submit = async () => {
    if (!form.make || !form.model) { toast.error("Make and Model are required"); return; }
    const payload = { ...form };
    ["year", "mileage", "purchase_price", "auction_fees", "transport_fees", "gate_fees", "documentation_fees", "storage_fees", "buyer_fees", "misc_costs", "market_value"]
      .forEach((k) => (payload[k] = Number(payload[k] || 0)));
    try {
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vehicle? Cascades repairs, sales and documents.")) return;
    await api.delete(`/vehicles/${id}`); toast.success("Vehicle deleted"); load();
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader
        overline="Fleet"
        title="Inventory"
        description="Every vehicle from purchase to delivery."
        action={canEdit && (
          <Button data-testid="add-vehicle-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
            <Plus size={16} className="mr-2" /> Add Vehicle
          </Button>
        )}
      />

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="inventory-search" value={q} onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}); }} placeholder="Search VIN, make, model, stock…" className="pl-9 rounded-lg" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="inventory-status-filter" className="rounded-lg w-full md:w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {VEHICLE_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-x-auto" data-testid="inventory-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-xs uppercase tracking-wider text-muted-foreground">
              {["Stock", "Vehicle", "VIN", "Mileage", "Investment", "Status", "Age", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No vehicles yet.</td></tr>}
            {filtered.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.id}`} className="border-t border-border hover:bg-secondary/50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">{v.stock_number}</td>
                <td className="px-4 py-3">
                  <Link to={`/inventory/${v.id}`} className="font-semibold text-foreground hover:text-blue-600">{v.year} {v.make} {v.model}</Link>
                  <div className="text-xs text-muted-foreground">{v.trim || ""} · {v.color || "—"}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{v.vin || "—"}</td>
                <td className="px-4 py-3">{Number(v.mileage || 0).toLocaleString()} mi</td>
                <td className="px-4 py-3 font-semibold">{fmtMoney(v.total_investment)}</td>
                <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-3 text-muted-foreground">{v.age_days}d</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {canEdit && <button data-testid={`edit-vehicle-${v.id}`} onClick={() => { setEditing(v); setForm({ ...EMPTY, ...v }); setOpen(true); }} className="p-1.5 rounded-md hover:bg-secondary"><Pencil size={14} /></button>}
                    {canDelete && <button data-testid={`delete-vehicle-${v.id}`} onClick={() => remove(v.id)} className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
            <DialogDescription>Enter purchase, vehicle and fee details. Use VIN auto-decode to populate.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <section>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">VIN & Identity</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 flex gap-2">
                  <div className="flex-1">
                    <Label>VIN</Label>
                    <Input data-testid="v-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} className="rounded-lg font-mono" />
                  </div>
                  <Button type="button" data-testid="v-decode" onClick={decodeVin} disabled={decoding} className="self-end bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                    {decoding ? <Loader2 size={14} className="animate-spin mr-2" /> : <Wand2 size={14} className="mr-2" />}
                    Decode
                  </Button>
                </div>
                {[["year", "Year", "number"], ["make", "Make"], ["model", "Model"], ["trim", "Trim"], ["mileage", "Mileage", "number"], ["color", "Color"], ["engine", "Engine"], ["transmission", "Transmission"], ["fuel_type", "Fuel Type"]].map(([k, l, t]) => (
                  <div key={k}>
                    <Label>{l}</Label>
                    <Input data-testid={`v-${k}`} type={t || "text"} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-lg" />
                  </div>
                ))}
                <div>
                  <Label>Title Status</Label>
                  <Select value={form.title_status} onValueChange={(v) => setForm({ ...form, title_status: v })}>
                    <SelectTrigger data-testid="v-title-status" className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{TITLE_STATUS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Purchase</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Purchase Date</Label>
                  <Input data-testid="v-purchase-date" type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="rounded-lg" />
                </div>
                <div>
                  <Label>Purchase Price</Label>
                  <Input data-testid="v-purchase-price" type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-lg" />
                </div>
                <div>
                  <Label>Auction House</Label>
                  <Select value={form.auction_house || "none"} onValueChange={(v) => setForm({ ...form, auction_house: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="v-auction" className="rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {AUCTION_HOUSES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Purchase Location</Label>
                  <Input data-testid="v-purchase-location" value={form.purchase_location} onChange={(e) => setForm({ ...form, purchase_location: e.target.value })} className="rounded-lg" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Seller Info</Label>
                  <Input data-testid="v-seller" value={form.seller_info} onChange={(e) => setForm({ ...form, seller_info: e.target.value })} className="rounded-lg" />
                </div>
              </div>
            </section>

            <section>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Itemized Auction Fees</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["auction_fees", "Auction"], ["transport_fees", "Transport"], ["gate_fees", "Gate"], ["documentation_fees", "Doc"], ["storage_fees", "Storage"], ["buyer_fees", "Buyer"], ["misc_costs", "Misc"], ["market_value", "Market Value"]].map(([k, l]) => (
                  <div key={k}>
                    <Label>{l}</Label>
                    <Input data-testid={`v-${k}`} type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-lg" />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="v-status" className="rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>{VEHICLE_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label>Notes</Label>
                <Textarea data-testid="v-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg" />
              </div>
            </section>
          </div>

          <DialogFooter>
            <Button data-testid="v-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
